/**
 * Admin User Table Component
 *
 * Table display for admin user list with actions.
 *
 * @fileoverview Admin user data table component
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Box,
  Text,
  Skeleton,
  useColorModeValue,
  Icon,
  Flex,
  Button,
  Tooltip,
  Avatar,
  HStack,
  Tag,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  MdMoreVert,
  MdVisibility,
  MdEdit,
  MdPersonOff,
  MdPersonAdd,
  MdDelete,
  MdStar,
  MdChevronLeft,
  MdChevronRight,
  MdLockReset,
  MdBusiness,
} from 'react-icons/md';
import type { AdminUser, PaginationState } from '../../types/admin';

interface AdminUserTableProps {
  /** List of users to display */
  users: AdminUser[];
  /** Loading state */
  loading: boolean;
  /** Pagination state */
  pagination: PaginationState;
  /** Current user ID (to disable certain actions) */
  currentUserId?: string;
  /** Page change callback */
  onPageChange: (page: number) => void;
  /** View user callback */
  onView: (user: AdminUser) => void;
  /** Edit user callback */
  onEdit: (user: AdminUser) => void;
  /** Activate user callback */
  onActivate: (user: AdminUser) => void;
  /** Deactivate user callback */
  onDeactivate: (user: AdminUser) => void;
  /** Delete user callback */
  onDelete: (user: AdminUser) => void;
  /** Send password reset callback */
  onPasswordReset: (user: AdminUser) => void;
  /** Manage memberships callback */
  onManageMemberships: (user: AdminUser) => void;
}

/**
 * Get role badge for user
 *
 * @param globalRole - User's global role
 * @returns Badge color scheme and label
 */
function getRoleBadge(globalRole: string) {
  if (globalRole === 'ADMIN') {
    return { colorScheme: 'purple', label: 'Admin' };
  }
  return { colorScheme: 'gray', label: 'User' };
}

/**
 * Get status badge for user
 *
 * @param isActive - User's active status
 * @returns Badge color scheme and label
 */
function getStatusBadge(isActive: boolean) {
  return isActive
    ? { colorScheme: 'green', label: 'Active' }
    : { colorScheme: 'red', label: 'Inactive' };
}

/**
 * Get display name for user
 *
 * @param user - Admin user object
 * @returns Display name string
 */
function getDisplayName(user: AdminUser): string {
  if (user.displayName) return user.displayName;
  if (user.firstName || user.lastName) {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
  return user.email.split('@')[0];
}

/**
 * Admin User Table Component
 *
 * Displays users in a table with actions.
 *
 * @param props - Component props
 * @returns React component
 */
export const AdminUserTable: React.FC<AdminUserTableProps> = ({
  users,
  loading,
  pagination,
  currentUserId,
  onPageChange,
  onView,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
  onPasswordReset,
  onManageMemberships,
}) => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  /**
   * Format date for display
   */
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  /**
   * Handle row click - navigate to user details
   */
  const handleRowClick = (user: AdminUser) => {
    navigate(`/admin/users/${user.id}`);
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
        <Table>
          <Thead>
            <Tr>
              <Th>User</Th>
              <Th>Tenants</Th>
              <Th>Tenant Memberships</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {[1, 2, 3, 4, 5].map((i) => (
              <Tr key={i}>
                <Td><Skeleton h="40px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" w="60px" /></Td>
                <Td><Skeleton h="20px" w="60px" /></Td>
                <Td><Skeleton h="20px" /></Td>
                <Td><Skeleton h="20px" w="40px" /></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  }

  // Empty state
  if (users.length === 0) {
    return (
      <Box
        bg={bgColor}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        p={8}
        textAlign="center"
      >
        <Text color={mutedColor}>No users found</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor} overflow="hidden">
        <Table>
          <Thead>
            <Tr>
              <Th>User</Th>
              <Th>Tenants</Th>
              <Th>Tenant Memberships</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => {
              const role = getRoleBadge(user.globalRole);
              const status = getStatusBadge(user.isActive);
              const isCurrentUser = user.id === currentUserId;

              return (
                <Tr
                  key={user.id}
                  _hover={{ bg: hoverBg }}
                  cursor="pointer"
                  onClick={() => handleRowClick(user)}
                >
                  <Td>
                    <HStack spacing={3}>
                      <Avatar
                        size="sm"
                        name={getDisplayName(user)}
                      />
                      <Box>
                        <HStack>
                          <Text fontWeight="medium">
                            {getDisplayName(user)}
                          </Text>
                          {user.globalRole === 'ADMIN' && (
                            <Icon as={MdStar} color="yellow.400" boxSize={4} />
                          )}
                          {isCurrentUser && (
                            <Badge colorScheme="teal" fontSize="xs">You</Badge>
                          )}
                        </HStack>
                        <Text fontSize="sm" color={mutedColor}>
                          {user.email}
                        </Text>
                      </Box>
                    </HStack>
                  </Td>
                  <Td>
                    {user.memberships && user.memberships.length > 0 ? (
                      <Wrap spacing={1}>
                        {user.memberships.slice(0, 3).map((m) => (
                          <WrapItem key={m.tenantId}>
                            <Tooltip label={`${m.tenantSlug} • ${m.tenantRole === 'TENANT_ADMIN' ? 'Admin' : 'User'}${m.isOwner ? ' • Owner' : ''}`}>
                              <Text fontSize="sm" fontWeight="medium" cursor="pointer" _hover={{ textDecoration: 'underline' }}>
                                {m.tenantName}
                              </Text>
                            </Tooltip>
                          </WrapItem>
                        ))}
                        {user.memberships.length > 3 && (
                          <WrapItem>
                            <Tooltip label={`${user.memberships.length - 3} more tenant${user.memberships.length - 3 > 1 ? 's' : ''}`}>
                              <Text fontSize="sm" color={mutedColor}>
                                +{user.memberships.length - 3}
                              </Text>
                            </Tooltip>
                          </WrapItem>
                        )}
                      </Wrap>
                    ) : (
                      <Text fontSize="sm" color={mutedColor}>—</Text>
                    )}
                  </Td>
                  <Td>
                    {user.memberships && user.memberships.length > 0 ? (
                      <Wrap spacing={1}>
                        {user.memberships.slice(0, 2).map((m) => {
                          const roleLabel = m.tenantRole === 'TENANT_ADMIN' ? 'Admin' : 'User';
                          const ownerSuffix = m.isOwner ? ' - Owner' : '';
                          const tooltipText = `${m.tenantSlug} • Joined ${formatDate(m.joinedAt)}`;
                          
                          return (
                            <WrapItem key={m.tenantId}>
                              <Tooltip label={tooltipText}>
                                <Tag
                                  size="sm"
                                  colorScheme={m.isOwner ? 'purple' : m.tenantRole === 'TENANT_ADMIN' ? 'blue' : 'gray'}
                                >
                                  {m.tenantName} ({roleLabel}{ownerSuffix})
                                </Tag>
                              </Tooltip>
                            </WrapItem>
                          );
                        })}
                        {user.memberships.length > 2 && (
                          <WrapItem>
                            <Tooltip label={`${user.memberships.length - 2} more tenant${user.memberships.length - 2 > 1 ? 's' : ''}`}>
                              <Tag size="sm" colorScheme="gray">
                                +{user.memberships.length - 2}
                              </Tag>
                            </Tooltip>
                          </WrapItem>
                        )}
                      </Wrap>
                    ) : (
                      <Text fontSize="sm" color={mutedColor}>No tenants</Text>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={role.colorScheme} variant="subtle">
                      {role.label}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={status.colorScheme} variant="subtle">
                      {status.label}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color={mutedColor}>
                      {formatDate(user.createdAt)}
                    </Text>
                  </Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<Icon as={MdMoreVert} />}
                        variant="ghost"
                        size="sm"
                        isDisabled={isCurrentUser}
                      />
                      <MenuList>
                        <MenuItem
                          icon={<Icon as={MdVisibility} />}
                          onClick={() => onView(user)}
                        >
                          View Details
                        </MenuItem>
                        <MenuItem
                          icon={<Icon as={MdEdit} />}
                          onClick={() => onEdit(user)}
                        >
                          Edit User
                        </MenuItem>
                        <MenuItem
                          icon={<Icon as={MdBusiness} />}
                          onClick={() => onManageMemberships(user)}
                        >
                          Manage Organizations
                        </MenuItem>
                        {user.isActive ? (
                          <MenuItem
                            icon={<Icon as={MdPersonOff} />}
                            onClick={() => onDeactivate(user)}
                          >
                            Deactivate
                          </MenuItem>
                        ) : (
                          <MenuItem
                            icon={<Icon as={MdPersonAdd} />}
                            onClick={() => onActivate(user)}
                          >
                            Activate
                          </MenuItem>
                        )}
                        <MenuItem
                          icon={<Icon as={MdLockReset} />}
                          onClick={() => onPasswordReset(user)}
                        >
                          Send Password Reset
                        </MenuItem>
                        <MenuItem
                          icon={<Icon as={MdDelete} />}
                          color="red.500"
                          onClick={() => onDelete(user)}
                        >
                          Delete User
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Pagination */}
      <Flex justify="space-between" align="center" mt={4}>
        <Text fontSize="sm" color={mutedColor}>
          Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
          {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
          {pagination.total} users
        </Text>
        <Flex gap={2}>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={MdChevronLeft} />}
            isDisabled={pagination.page <= 1}
            onClick={() => onPageChange(pagination.page - 1)}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="outline"
            rightIcon={<Icon as={MdChevronRight} />}
            isDisabled={pagination.page >= pagination.totalPages}
            onClick={() => onPageChange(pagination.page + 1)}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default AdminUserTable;
